/**
 * The Glass — the truth pane (wave glass-3, #795; THE GLASS ruled #786).
 *
 * A sub-route of The Glass: the register — every front file, its kind, state,
 * verdict, and the AGE of the verdict — rendered VERBATIM from the packaged
 * canon (GLASS_CANON.register.rows, the YAML parse of
 * engineering/contract/verified.yaml carried whole at build time; keys
 * untouched, snake_case stays — no translation layer).
 *
 * The glass constitution (design-786 §2.10) governs: render canon, never
 * author state — no sentence on this page describes how things stand except
 * canon text carried verbatim; pane prose is mechanical labels only (counts,
 * dates, field names, renders-<source> lines). Freshness is DISPLAYED, never
 * auto-judged: verdict ages are checked_on DATES the reader ages by eye — no
 * age threshold, no computed age, no color verdict, no flag (the ruled entry,
 * execution-plan.yaml the_glass → glass-3). Sorting by checked_on is offered
 * as a reader's act; the page judges nothing.
 */

import { useMemo, useState } from "react";
import { GLASS_CANON, type GlassRegisterRow } from "@command-center/shared";
import { cn } from "@/lib/utils";

/** Group id: the row path's first two segments (`engineering/<front>/`);
 *  a file sitting directly under engineering/ groups as `engineering/`.
 *  A projection of the paths, not an authored list. */
function groupOf(path: string): string {
  const parts = path.split("/");
  return parts.length > 2 ? `${parts[0]}/${parts[1]}/` : `${parts[0]}/`;
}

type SortMode = "path" | "checked_on";

export function GlassTruthPage() {
  const {
    rows,
    rowCount,
    tally,
    generatedOn,
    oldestCheckedOn,
    newestCheckedOn,
  } = GLASS_CANON.register;
  const [filter, setFilter] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("path");

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return needle === ""
      ? rows
      : rows.filter((r) => r.path.toLowerCase().includes(needle));
  }, [rows, filter]);

  const groups = useMemo(() => {
    const byGroup = new Map<string, GlassRegisterRow[]>();
    for (const r of filtered) {
      const g = groupOf(r.path);
      const list = byGroup.get(g);
      if (list) list.push(r);
      else byGroup.set(g, [r]);
    }
    return [...byGroup.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const flatByDate = useMemo(
    () =>
      sortMode === "checked_on"
        ? [...filtered].sort(
            (a, b) =>
              a.checked_on.localeCompare(b.checked_on) ||
              a.path.localeCompare(b.path),
          )
        : null,
    [filtered, sortMode],
  );

  return (
    <section className="mx-auto max-w-6xl p-6">
      <h1 className="mb-1 text-xl tracking-tight">The Glass — Truth</h1>
      <p className="mb-1 text-sm text-plane-muted">
        The register — every front file, its kind, state, verdict, and the
        date of the verdict, rendered verbatim from the canon file named
        below.
      </p>
      <p className="mb-5 text-xs text-plane-muted">
        renders <code>{GLASS_CANON.meta.sources.register}</code>, packaged at
        build time; this page renders it read-only.
      </p>

      <div className="mb-6 rounded-md border border-border bg-muted p-4 text-sm">
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
          Register
        </div>
        <span className="font-medium">{rowCount}</span> rows (
        {tally.clean} clean · {tally.swept} swept) · generated_on{" "}
        {generatedOn} · checked_on {oldestCheckedOn} … {newestCheckedOn}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 text-xs">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filter by path"
          className="w-64 rounded border border-border bg-card px-2 py-1"
          data-testid="glass-truth-filter"
        />
        <span className="text-plane-muted">
          {filtered.length} of {rows.length} rows
        </span>
        <span className="text-plane-muted">sort:</span>
        <button
          type="button"
          className={cn(
            "rounded border border-border px-2 py-0.5 hover:bg-muted",
            sortMode === "path" && "bg-muted font-medium",
          )}
          onClick={() => setSortMode("path")}
        >
          path (grouped by front)
        </button>
        <button
          type="button"
          className={cn(
            "rounded border border-border px-2 py-0.5 hover:bg-muted",
            sortMode === "checked_on" && "bg-muted font-medium",
          )}
          onClick={() => setSortMode("checked_on")}
        >
          checked_on (oldest first)
        </button>
      </div>

      {flatByDate !== null ? (
        <RowTable rows={flatByDate} testid="glass-truth-by-date" />
      ) : (
        groups.map(([group, groupRows]) => (
          <div key={group} className="mb-6">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
              {group} · {groupRows.length}{" "}
              {groupRows.length === 1 ? "row" : "rows"}
            </div>
            <RowTable rows={groupRows} testid={`glass-truth-${group}`} />
          </div>
        ))
      )}
    </section>
  );
}

function RowTable({
  rows,
  testid,
}: {
  rows: GlassRegisterRow[];
  testid: string;
}) {
  const [openPath, setOpenPath] = useState<string | null>(null);

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full border-collapse text-left text-xs" data-testid={testid}>
        <thead>
          <tr className="border-b border-border text-plane-muted">
            <th className="px-2 py-1.5 font-medium">path</th>
            <th className="px-2 py-1.5 font-medium">kind</th>
            <th className="px-2 py-1.5 font-medium">state</th>
            <th className="px-2 py-1.5 font-medium">verdict</th>
            <th className="px-2 py-1.5 font-medium">checked_on</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const open = openPath === r.path;
            return (
              <RegisterRow
                key={r.path}
                row={r}
                open={open}
                onToggle={() => setOpenPath(open ? null : r.path)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RegisterRow({
  row,
  open,
  onToggle,
}: {
  row: GlassRegisterRow;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={cn(
          "cursor-pointer border-b border-border/60 hover:bg-muted",
          open && "bg-muted",
        )}
        onClick={onToggle}
      >
        <td className="px-2 py-1.5">
          <code>{row.path}</code>
        </td>
        <td className="whitespace-nowrap px-2 py-1.5">{row.kind}</td>
        <td className="whitespace-nowrap px-2 py-1.5">{row.state}</td>
        <td className="whitespace-nowrap px-2 py-1.5">{row.verdict}</td>
        <td className="whitespace-nowrap px-2 py-1.5">{row.checked_on}</td>
      </tr>
      {open ? (
        <tr className="border-b border-border/60 bg-muted/50">
          <td colSpan={5} className="px-3 py-2">
            <dl className="space-y-1 text-xs">
              <RowField label="check" value={row.check} />
              <RowField label="checker" value={row.checker} />
              <RowField label="rows_at" value={row.rows_at} mono />
              {row.failing_claim !== undefined ? (
                <RowField label="failing_claim" value={row.failing_claim} />
              ) : null}
              {row.demoted !== undefined ? (
                <RowField label="demoted" value={row.demoted} />
              ) : null}
              {row.repair_chain !== undefined ? (
                <RowField label="repair_chain" value={row.repair_chain} />
              ) : null}
            </dl>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function RowField({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="inline font-medium text-plane-muted">{label}: </dt>
      <dd
        className={cn(
          "inline whitespace-pre-wrap",
          mono === true && "font-mono",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
